'use client';

import { useState, useEffect } from 'react';
import { useRouter } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import { usePremium } from '@/contexts/PremiumContext';
import { getRandomQuestions, QuizQuestion } from '@/data/quizQuestions';
import { GraduationCap, Trophy, RotateCcw, Home, CheckCircle, XCircle, Crown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslations } from 'next-intl';

export default function QuizPage() {
  const { isPremium } = usePremium();
  const router = useRouter();
  const t = useTranslations('Quiz');

  const [quizStarted, setQuizStarted] = useState(false);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [userAnswers, setUserAnswers] = useState<(number | null)[]>([]);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');

  useEffect(() => {
    if (!isPremium) {
      toast.error(t('errorPremiumOnly'));
      router.push('/premium');
    }
  }, [isPremium, router, t]);

  const startQuiz = () => {
    const newQuestions = getRandomQuestions(10, difficulty);
    setQuestions(newQuestions);
    setUserAnswers(new Array(newQuestions.length).fill(null));
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
    setShowResults(false);
    setQuizStarted(true);
  };

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNextQuestion = () => {
    if (selectedAnswer === null) {
      toast.error(t('errorSelectAnswer'));
      return;
    }

    const newUserAnswers = [...userAnswers];
    newUserAnswers[currentQuestion] = selectedAnswer;
    setUserAnswers(newUserAnswers);

    if (selectedAnswer === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      setShowResults(true);
    }
  };

  const resetQuiz = () => {
    setQuizStarted(false);
    setShowResults(false);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
  };

  if (!isPremium) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <Crown className="w-24 h-24 text-[#FFD700] mx-auto mb-6" />
        <h1 className="text-4xl font-bold text-white mb-4">{t('premiumTitle')}</h1>
        <p className="text-[#a8b2d1] text-xl mb-8">{t('premiumDesc')}</p>
        <Button
          onClick={() => router.push('/premium')}
          className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0a192f] hover:opacity-90 font-bold"
        >
          <Crown className="w-5 h-5 mr-2" />
          {t('upgradeToPremium')}
        </Button>
      </div>
    );
  }

  if (!quizStarted) {
    const difficulties = [
      { value: 'easy' as const, labelKey: 'easy' as const, descKey: 'easyDesc' as const, color: '#4ade80' },
      { value: 'medium' as const, labelKey: 'medium' as const, descKey: 'mediumDesc' as const, color: '#60a5fa' },
      { value: 'hard' as const, labelKey: 'hard' as const, descKey: 'hardDesc' as const, color: '#f87171' },
    ];

    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <div className="text-center mb-12">
          <GraduationCap className="w-20 h-20 text-[#64ffda] mx-auto mb-6" />
          <h1 className="text-5xl font-bold text-white mb-4">{t('title')}</h1>
          <p className="text-xl text-[#a8b2d1]">{t('subtitle')}</p>
        </div>

        <div className="bg-[#112240] border-2 border-[#233554] rounded-xl p-8">
          <h2 className="text-2xl font-bold text-[#64ffda] mb-6">{t('chooseDifficulty')}</h2>

          <div className="grid gap-4 mb-8">
            {difficulties.map((diff) => (
              <button
                key={diff.value}
                onClick={() => setDifficulty(diff.value)}
                className={`p-6 rounded-lg border-2 text-left transition-all ${
                  difficulty === diff.value
                    ? 'border-[#64ffda] bg-[#64ffda]/10'
                    : 'border-[#233554] hover:border-[#64ffda]/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{t(diff.labelKey)}</h3>
                    <p className="text-sm text-[#a8b2d1]">{t(diff.descKey)}</p>
                  </div>
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: diff.color }} />
                </div>
              </button>
            ))}
          </div>

          <Button
            onClick={startQuiz}
            className="w-full bg-gradient-to-r from-[#64ffda] to-[#82aaff] text-[#0a192f] hover:opacity-90 font-bold text-lg py-6"
          >
            <GraduationCap className="w-5 h-5 mr-2" />
            {t('startQuiz')}
          </Button>
        </div>
      </div>
    );
  }

  if (showResults) {
    const percentage = Math.round((score / questions.length) * 100);
    let message = '';
    let color = '';

    if (percentage >= 90) {
      message = t('excellent');
      color = '#4ade80';
    } else if (percentage >= 70) {
      message = t('veryGood');
      color = '#60a5fa';
    } else if (percentage >= 50) {
      message = t('notBad');
      color = '#fbbf24';
    } else {
      message = t('keepStudying');
      color = '#f87171';
    }

    return (
      <div className="container mx-auto px-4 py-16 max-w-3xl">
        <div className="text-center mb-12">
          <Trophy className="w-24 h-24 mx-auto mb-6" style={{ color }} />
          <h1 className="text-5xl font-bold text-white mb-4">{t('resultsTitle')}</h1>
          <div className="text-6xl font-bold mb-4" style={{ color }}>
            {score}/{questions.length}
          </div>
          <p className="text-2xl font-semibold mb-2" style={{ color }}>{percentage}%</p>
          <p className="text-xl text-[#a8b2d1]">{message}</p>
        </div>

        <div className="space-y-4 mb-8">
          {questions.map((q, index) => {
            const userAnswer = userAnswers[index];
            const isCorrect = userAnswer === q.correctAnswer;

            return (
              <div
                key={q.id}
                className={`p-6 rounded-lg border-2 ${
                  isCorrect ? 'border-green-500/50 bg-green-500/10' : 'border-red-500/50 bg-red-500/10'
                }`}
              >
                <div className="flex items-start gap-4">
                  {isCorrect ? (
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1">
                    <p className="text-white font-semibold mb-2">
                      {index + 1}. {q.question}
                    </p>
                    <p className="text-sm text-[#a8b2d1] mb-2">
                      <span className="font-semibold">{t('yourAnswer')}</span>{' '}
                      {userAnswer !== null ? q.options[userAnswer] : t('noAnswer')}
                    </p>
                    {!isCorrect && (
                      <p className="text-sm text-green-400">
                        <span className="font-semibold">{t('correctAnswer')}</span>{' '}
                        {q.options[q.correctAnswer]}
                      </p>
                    )}
                    {q.explanation && (
                      <p className="text-sm text-[#a8b2d1] mt-2 italic">
                        💡 {q.explanation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-4">
          <Button
            onClick={resetQuiz}
            className="flex-1 bg-gradient-to-r from-[#64ffda] to-[#82aaff] text-[#0a192f] hover:opacity-90"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('restart')}
          </Button>
          <Button
            onClick={() => router.push('/')}
            variant="outline"
            className="flex-1 border-[#64ffda] text-[#64ffda] hover:bg-[#64ffda]/10"
          >
            <Home className="w-4 h-4 mr-2" />
            {t('home')}
          </Button>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = Math.round(((currentQuestion + 1) / questions.length) * 100);

  return (
    <div className="container mx-auto px-4 py-16 max-w-3xl">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[#a8b2d1]">
            {t('questionOf', { current: currentQuestion + 1, total: questions.length })}
          </span>
          <span className="text-[#64ffda] font-semibold">
            {t('score')} {score}/{currentQuestion}
          </span>
        </div>
        <div className="w-full bg-[#233554] rounded-full h-3">
          <div
            className="bg-gradient-to-r from-[#64ffda] to-[#82aaff] h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="bg-[#112240] border-2 border-[#233554] rounded-xl p-8 mb-8">
        <div className="mb-6">
          <span className="px-3 py-1 bg-[#64ffda]/20 text-[#64ffda] rounded-full text-sm font-semibold">
            {question.difficulty === 'easy' ? t('easy') : question.difficulty === 'medium' ? t('medium') : t('hard')}
          </span>
        </div>

        <h2 className="text-2xl font-bold text-white mb-8">{question.question}</h2>

        <div className="space-y-4">
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              className={`w-full p-5 rounded-lg border-2 text-left transition-all ${
                selectedAnswer === index
                  ? 'border-[#64ffda] bg-[#64ffda]/20 text-white'
                  : 'border-[#233554] text-[#a8b2d1] hover:border-[#64ffda]/50 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                    selectedAnswer === index ? 'border-[#64ffda] bg-[#64ffda]' : 'border-[#a8b2d1]'
                  }`}
                >
                  {selectedAnswer === index && (
                    <CheckCircle className="w-5 h-5 text-[#0a192f]" />
                  )}
                </div>
                <span className="flex-1 font-medium">{option}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={handleNextQuestion}
        disabled={selectedAnswer === null}
        className="w-full bg-gradient-to-r from-[#64ffda] to-[#82aaff] text-[#0a192f] hover:opacity-90 font-bold text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {currentQuestion < questions.length - 1 ? t('nextQuestion') : t('seeResults')}
      </Button>
    </div>
  );
}
